/**
 * NewOrderFromPoModal
 * -------------------
 * Drop a customer PO on the Sourcing Desk and get the whole order record built
 * from it — customer, ship-to, bill-to, buyer, terms, freight and every line.
 * AskIsaac reads the document; you confirm before anything is written.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Wand2, FileScan, AlertTriangle, CheckCircle2, Building2, Eye,
  Search, Link2, Sparkles, X, MapPin, Truck,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL;

const Field = ({ label, value }) => (
  value ? (
    <div>
      <span className="block text-[9px] font-black uppercase tracking-wider text-slate-400">{label}</span>
      <span className="text-[12px] text-slate-800">{value}</span>
    </div>
  ) : null
);

export default function NewOrderFromPoModal({ open, onOpenChange, token, onCreated }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [reading, setReading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    hospital_id: "", hospital_name: "", contact_name: "", contact_email: "",
    freight_percent: 20,
  });
  const [allowDuplicate, setAllowDuplicate] = useState(false);
  // Which hospital in our database this PO belongs to. Their PO name is often
  // a corporate or per-location entity we never registered, so this is an
  // explicit confirmation, not a guess we act on.
  const [hospital, setHospital] = useState(null);       // confirmed record
  const [newCustomer, setNewCustomer] = useState(false); // "not in our database"
  const [hospQuery, setHospQuery] = useState("");
  const [hospResults, setHospResults] = useState([]);
  const [hospSearching, setHospSearching] = useState(false);
  const [aliasLearned, setAliasLearned] = useState(null);
  // Which facility of that system the PO ships to. Ben Taub and LBJ are the
  // same customer and two different docks, so this is confirmed separately.
  const [facMatch, setFacMatch] = useState(null);
  const [facility, setFacility] = useState(null);
  const [shipAsPrinted, setShipAsPrinted] = useState(false);
  const [facLoading, setFacLoading] = useState(false);
  // Who we are buying from. Optional — but doing it here saves a trip to the
  // award panel, and a manually typed supplier is saved permanently.
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [supplierEmail, setSupplierEmail] = useState("");
  const [supplierManual, setSupplierManual] = useState(false);

  const reset = () => {
    setResult(null);
    setAllowDuplicate(false);
    setHospital(null);
    setNewCustomer(false);
    setHospQuery("");
    setHospResults([]);
    setAliasLearned(null);
    setFacMatch(null);
    setFacility(null);
    setShipAsPrinted(false);
    setSupplierId("");
    setSupplierName("");
    setSupplierEmail("");
    setSupplierManual(false);
    setForm({ hospital_id: "", hospital_name: "", contact_name: "", contact_email: "", freight_percent: 20 });
  };

  // Supplier list for the award dropdown.
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/api/suppliers`, { headers })
      .then((r) => setSuppliers(r.data?.suppliers || r.data || []))
      .catch(() => setSuppliers([]));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [open]);

  // Live search of our hospital database — by our name, their registered legal
  // entity, or any facility under the system.
  useEffect(() => {
    if (!result || hospital || newCustomer) return;
    let cancelled = false;
    setHospSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(
          `${API}/api/admin/po-intake/hospital-search?q=${encodeURIComponent(hospQuery)}`,
          { headers });
        if (!cancelled) setHospResults(r.data?.hospitals || []);
      } catch {
        if (!cancelled) setHospResults([]);
      } finally {
        if (!cancelled) setHospSearching(false);
      }
    }, hospQuery ? 300 : 0);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, hospQuery, hospital, newCustomer]);

  const confirmHospital = (h) => {
    setHospital(h);
    setNewCustomer(false);
    setForm((f) => ({
      ...f,
      hospital_id: h.id,
      hospital_name: h.name,
      contact_email: f.contact_email || h.email || "",
    }));
  };

  // Once we know the system, work out which of its docks the PO ships to.
  useEffect(() => {
    if (!result || !hospital?.id) { setFacMatch(null); setFacility(null); return; }
    let cancelled = false;
    const p0 = result.proposal || {};
    setFacLoading(true);
    (async () => {
      try {
        const r = await axios.get(
          `${API}/api/admin/po-intake/facility-match`
          + `?hospital_id=${encodeURIComponent(hospital.id)}`
          + `&ship_to=${encodeURIComponent(p0.ship_to || "")}`
          + `&facility=${encodeURIComponent(p0.ship_to_facility || "")}`,
          { headers });
        if (cancelled) return;
        const fm = r.data || null;
        setFacMatch(fm);
        // Only a dock a human mapped before, or the system's single facility,
        // is trusted enough to pre-select.
        if (fm?.resolved && (fm.source === "alias" || fm.source === "only_facility")) {
          setFacility(fm.resolved);
          if (fm.source === "alias") {
            toast.info(`Shipping to ${fm.resolved.name}`, {
              description: "You mapped this ship-to block to that facility before.",
            });
          }
        } else {
          setFacility(null);
        }
      } catch {
        if (!cancelled) { setFacMatch(null); setFacility(null); }
      } finally {
        if (!cancelled) setFacLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, hospital?.id]);

  const read = async (file) => {
    if (!file) return;
    setReading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/api/admin/po-intake/parse`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      const p = r.data?.proposal || {};
      const cm = r.data?.customer_match || {};
      setForm((f) => ({
        ...f,
        hospital_name: p.customer_name || "",
        contact_name: p.buyer_name || "",
        contact_email: p.buyer_email || "",
        hospital_id: "",
      }));
      setHospQuery(p.customer_name || "");
      // Only an alias a human confirmed before is trusted enough to pre-confirm.
      if (cm.source === "alias" && cm.resolved) {
        confirmHospital(cm.resolved);
        toast.info(`Filed under ${cm.resolved.name}`, {
          description: `You mapped \u201c${p.customer_name}\u201d to them before.`,
        });
      } else {
        setHospital(null);
      }
      toast.success(`AskIsaac read PO ${p.po_number || "(no number)"}`, {
        description: `${(p.line_items || []).length} line(s) · $${Number(p.po_total || 0).toLocaleString()}`,
      });
      if ((r.data?.duplicates || []).length) {
        toast.error(`PO ${p.po_number} is already on ${r.data.duplicates.map(d => d.backorder_id).join(", ")}`, {
          description: "Open that order instead unless this is a revision.",
          duration: 12000,
        });
      }
    } catch (e) {
      toast.error(`Could not read that PO: ${e?.response?.data?.detail || e.message}`);
    } finally { setReading(false); }
  };

  const create = async () => {
    if (!hospital && !newCustomer) {
      toast.error("Confirm which hospital this PO belongs to", {
        description: "Pick the hospital system, or tick \u201cnot in our database\u201d.",
      });
      return;
    }
    if (!form.hospital_name.trim()) { toast.error("Who is this order for?"); return; }
    if (needsShipConfirm) {
      toast.error("Confirm which facility this PO ships to", {
        description: `${hospital.name} has ${facMatch.facility_count} facilities on file — `
          + "pick the dock, or tick “ship to the address as printed”.",
      });
      return;
    }
    setCreating(true);
    try {
      const r = await axios.post(`${API}/api/admin/po-intake/create-order`, {
        proposal: result.proposal,
        file: result.file,
        hospital_id: hospital?.id || null,
        hospital_name: hospital?.name || form.hospital_name,
        confirm_new_customer: newCustomer,
        remember_alias: true,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        freight_percent: Number(form.freight_percent) || 0,
        notify_hospital: false,
        allow_duplicate_po: allowDuplicate,
        ship_to_facility_id: facility?.id || null,
        confirm_ship_to_as_printed: shipAsPrinted,
        remember_ship_to_alias: true,
      }, { headers });
      toast.success(`Order ${r.data.backorder_id} built from the PO`, {
        description: [
          r.data?.ship_to_facility ? `Shipping to ${r.data.ship_to_facility.name}.` : "",
          r.data?.alias_learned
            ? `Remembered: POs saying \u201c${r.data.alias_learned.alias_name}\u201d are ${r.data.alias_learned.hospital_name}.`
            : "No notifications went out — review the Build Order tab, then confirm.",
        ].filter(Boolean).join(" "),
        duration: 9000,
      });
      setAliasLearned(r.data?.alias_learned || null);

      // Optional: stamp who we are buying from, so the award is done in the
      // same pass. A supplier typed by hand is saved to the supplier list.
      const orderId = r.data.backorder_id;
      if (supplierName.trim() && supplierEmail.trim()) {
        try {
          const a = await axios.post(
            `${API}/api/admin/backorders/${orderId}/email-supplier-award`,
            {
              supplier_id: supplierId || null,
              supplier_name: supplierName.trim(),
              supplier_email: supplierEmail.trim().toLowerCase(),
              send_email: false,
            }, { headers });
          toast.success(`Awarded to ${supplierName.trim()}`, {
            description: a.data?.supplier_created
              ? "New supplier saved — pick them from the list next time. No email was sent."
              : "Stamped on the order. No email was sent to the supplier.",
            duration: 8000,
          });
        } catch (err) {
          toast.error(`Order built, but the supplier award failed: ${err?.response?.data?.detail || err.message}`,
            { duration: 10000 });
        }
      }
      onCreated?.(r.data.backorder_id, r.data.backorder);
      reset();
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "customer_not_confirmed") {
        toast.error(detail.message, { duration: 12000 });
        setResult((r) => (r ? { ...r, customer_match: detail.customer_match } : r));
      } else if (e?.response?.status === 409 && detail?.code === "ship_to_not_confirmed") {
        toast.error(detail.message, { duration: 12000 });
        setFacMatch(detail.facility_match || null);
      } else if (e?.response?.status === 409 && detail?.code === "duplicate_po_number") {
        toast.error(detail.message, {
          description: "Tick “build it anyway” below if this PO is a revision.",
          duration: 14000,
        });
        setResult((r) => (r ? { ...r, duplicates: detail.duplicates || r.duplicates } : r));
      } else {
        toast.error(`Could not create the order: ${typeof detail === "object" ? detail?.message : detail || e.message}`);
      }
    } finally { setCreating(false); }
  };

  const p = result?.proposal;
  const customerReady = !!hospital || newCustomer;
  const needsShipConfirm = !!hospital && !!facMatch && facMatch.facility_count >= 2
    && !facility && !shipAsPrinted;
  const readyToBuild = customerReady && !needsShipConfirm && !facLoading;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="new-order-from-po-modal">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <FileScan className="w-4 h-4 text-violet-600" /> New order from a Purchase Order
          </DialogTitle>
          <DialogDescription>
            Upload the customer's PO. AskIsaac reads it and builds the order record — you confirm the
            customer and freight before anything is saved. No emails are sent.
          </DialogDescription>
        </DialogHeader>

        {!p ? (
          <label className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed
            py-12 cursor-pointer transition-colors ${reading
              ? "border-slate-300 bg-slate-50" : "border-violet-300 bg-violet-50/50 hover:bg-violet-50"}`}
            data-testid="po-dropzone">
            {reading ? (
              <>
                <Loader2 className="w-7 h-7 animate-spin text-violet-500" />
                <p className="text-sm font-semibold text-slate-700">AskIsaac is reading the PO…</p>
                <p className="text-xs text-slate-500">Usually about ten seconds.</p>
              </>
            ) : (
              <>
                <Wand2 className="w-7 h-7 text-violet-500" />
                <p className="text-sm font-semibold text-slate-800">Drop the PO here, or click to choose a file</p>
                <p className="text-xs text-slate-500">PDF or a photo of the PO · up to 20 MB</p>
              </>
            )}
            <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp"
              disabled={reading}
              onChange={(e) => { read(e.target.files?.[0]); e.target.value = ""; }}
              data-testid="po-standalone-file-input" />
          </label>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3">
              <p className="text-[11px] font-black uppercase tracking-wider text-violet-800 mb-2">
                What AskIsaac read
              </p>
              <div className="grid sm:grid-cols-4 gap-x-4 gap-y-2">
                <Field label="PO number" value={p.po_number} />
                <Field label="PO date" value={p.po_date} />
                <Field label="Need by" value={p.po_need_by} />
                <Field label="Terms" value={p.terms} />
                <Field label="Freight terms" value={p.freight_terms} />
                <Field label="Ship via" value={[p.ship_via, p.shipping_account].filter(Boolean).join(" · ")} />
                <Field label="Tax exempt" value={p.tax_exempt_id} />
                <Field label="PO total" value={p.po_total ? `$${Number(p.po_total).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : ""} />
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                {["ship_to", "bill_to"].map((k) => p[k] ? (
                  <div key={k} className="rounded bg-white border border-slate-200 p-2">
                    <span className="block text-[9px] font-black uppercase tracking-wider text-slate-400">
                      {k === "ship_to" ? "Ship to" : "Bill to"}
                    </span>
                    <span className="text-[11px] text-slate-700 whitespace-pre-line">{p[k]}</span>
                  </div>
                ) : null)}
              </div>
            </div>

            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="bg-slate-900 text-white text-[10px] font-black uppercase tracking-wider grid grid-cols-[90px_1fr_50px_50px_80px_90px] gap-2 px-2 py-1.5">
                <div>Item</div><div>Description</div><div className="text-center">Qty</div>
                <div>UOM</div><div className="text-right">Unit $</div><div className="text-right">Ext $</div>
              </div>
              {(p.line_items || []).map((li, i) => (
                <div key={i} className="grid grid-cols-[90px_1fr_50px_50px_80px_90px] gap-2 px-2 py-1.5 border-b border-slate-100 text-[11px]"
                  data-testid={`po-standalone-line-${i}`}>
                  <div className="font-mono text-slate-700 truncate">{li.sku || "—"}</div>
                  <div className="text-slate-800">
                    {li.name}
                    {li.notes ? <span className="block text-[10px] text-slate-400">{li.notes}</span> : null}
                  </div>
                  <div className="text-center">{li.quantity}</div>
                  <div>{li.uom}</div>
                  <div className="text-right">${Number(li.unit_price || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  <div className="text-right font-semibold">${Number(li.po_extended_price || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                </div>
              ))}
            </div>

            {(result?.duplicates || []).length > 0 && (
              <div className="rounded-lg border border-red-300 bg-red-50 p-3" data-testid="po-standalone-duplicate-warning">
                <p className="text-[11px] font-black uppercase tracking-wider text-red-800 inline-flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> This PO number is already on file
                </p>
                <ul className="mt-1 space-y-0.5">
                  {result.duplicates.map((d) => (
                    <li key={d.backorder_id} className="text-[12px] text-red-900">
                      <strong>{d.backorder_id}</strong>
                      {d.hospital_name ? ` · ${d.hospital_name}` : ""}
                      {d.po_number ? ` · PO ${d.po_number}` : ""}
                      {d.status ? ` · ${String(d.status).replace(/_/g, " ")}` : ""}
                      {d.order_total ? ` · $${Number(d.order_total).toLocaleString()}` : ""}
                      {d.match === "partial" ? " · near match" : ""}
                    </li>
                  ))}
                </ul>
                <label className="mt-2 flex items-center gap-2 text-[11px] font-semibold text-red-900 cursor-pointer">
                  <input type="checkbox" checked={allowDuplicate}
                    onChange={(e) => setAllowDuplicate(e.target.checked)}
                    data-testid="po-allow-duplicate-checkbox" />
                  This is a revision — build it anyway
                </label>
              </div>
            )}

            {/* ── Which hospital is this? Their PO name is rarely ours. ──────── */}
            <div className={`rounded-lg border p-3 ${hospital || newCustomer
              ? "border-emerald-300 bg-emerald-50/50" : "border-amber-300 bg-amber-50/60"}`}
              data-testid="po-hospital-confirm">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-wider inline-flex items-center gap-1.5
                    text-slate-700">
                    <Building2 className="w-3.5 h-3.5" />
                    Which hospital is this PO for?
                    {hospital || newCustomer
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      : <span className="text-amber-700">· confirmation required</span>}
                  </p>
                  <p className="text-[11px] text-slate-600 mt-0.5">
                    Their PO is billed to <strong>&ldquo;{p.customer_name || "—"}&rdquo;</strong>. POs often carry a
                    corporate or per-location name we do not file under, so pick the hospital system as it exists
                    in our database.
                  </p>
                </div>
                {result?.customer_match?.source === "alias" ? (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-white
                    ring-1 ring-emerald-300 rounded-full px-2 py-1 inline-flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Mapped before
                  </span>
                ) : null}
              </div>

              {hospital ? (
                <div className="mt-2 flex items-center justify-between gap-3 rounded bg-white ring-1 ring-emerald-300 p-2.5"
                  data-testid="po-hospital-chosen">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-900 inline-flex items-center gap-1.5">
                      <Link2 className="w-3.5 h-3.5 text-emerald-600" /> {hospital.name}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {[hospital.legal_entity_name && `Legal entity: ${hospital.legal_entity_name}`,
                        [hospital.city, hospital.state].filter(Boolean).join(", "),
                        hospital.facility_count ? `${hospital.facility_count} facility(ies)` : null,
                      ].filter(Boolean).join(" · ") || hospital.id}
                    </p>
                    {p.customer_name && p.customer_name.toLowerCase() !== (hospital.name || "").toLowerCase() ? (
                      <p className="text-[10px] text-emerald-800 mt-0.5">
                        We&rsquo;ll remember that POs saying &ldquo;{p.customer_name}&rdquo; are {hospital.name}.
                      </p>
                    ) : null}
                  </div>
                  <Button type="button" size="sm" variant="ghost"
                    className="h-7 px-2 text-[11px] text-slate-500 hover:bg-slate-100"
                    onClick={() => { setHospital(null); setForm((f) => ({ ...f, hospital_id: "" })); }}
                    data-testid="po-hospital-change-btn">
                    <X className="w-3 h-3 mr-1" /> Change
                  </Button>
                </div>
              ) : newCustomer ? (
                <div className="mt-2 rounded bg-white ring-1 ring-emerald-300 p-2.5">
                  <p className="text-[12px] text-slate-700">
                    Filing this order under <strong>{form.hospital_name || p.customer_name}</strong> as a new
                    customer — it will not be linked to a hospital record.
                  </p>
                </div>
              ) : (
                <>
                  {result?.customer_match?.resolved && (
                    <div className="mt-2 rounded bg-white ring-2 ring-cyan-400 p-2.5 flex items-start justify-between gap-3 flex-wrap"
                      data-testid="po-hospital-suggestion">
                      <div className="min-w-0">
                        <p className="text-[9px] font-black uppercase tracking-wider text-cyan-700">
                          {result.customer_match.source === "legal_entity"
                            ? "Their registered legal entity matches this hospital"
                            : "Closest match in our database"}
                        </p>
                        <p className="text-sm font-bold text-slate-900">{result.customer_match.resolved.name}</p>
                        <p className="text-[11px] text-slate-500">
                          {[result.customer_match.resolved.legal_entity_name
                              && `Legal entity: ${result.customer_match.resolved.legal_entity_name}`,
                            result.customer_match.resolved.is_network && "network",
                            result.customer_match.resolved.facility_count
                              ? `${result.customer_match.resolved.facility_count} facility(ies)` : null,
                          ].filter(Boolean).join(" · ")}
                        </p>
                      </div>
                      <Button type="button" size="sm"
                        className="bg-cyan-600 hover:bg-cyan-700 text-white h-8"
                        onClick={() => confirmHospital(result.customer_match.resolved)}
                        data-testid="po-hospital-confirm-suggestion-btn">
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Yes, that&rsquo;s them
                      </Button>
                    </div>
                  )}

                  {(result?.customer_match?.candidates || []).length > 0 && (
                    <div className="mt-2">
                      <p className="text-[9px] font-black uppercase tracking-wider text-slate-500 mb-1">
                        Closest matches in our database
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {result.customer_match.candidates.map((h) => (
                          <button key={h.id} type="button" onClick={() => confirmHospital(h)}
                            className="text-[11px] font-semibold px-2 py-1 rounded-full bg-white text-slate-700
                              ring-1 ring-amber-300 hover:ring-emerald-500 hover:text-emerald-800"
                            data-testid={`po-hospital-candidate-${h.id}`}>
                            {h.name}
                            {h.city ? <span className="opacity-60"> · {h.city}</span> : null}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-2">
                    <div className="flex items-center gap-2 rounded bg-white ring-1 ring-slate-300 px-2 py-1.5">
                      <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <input
                        value={hospQuery}
                        onChange={(e) => setHospQuery(e.target.value)}
                        placeholder="Search our hospital database — name, legal entity or facility"
                        className="flex-1 text-sm outline-none bg-transparent"
                        data-testid="po-hospital-search-input"
                      />
                      {hospSearching ? <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" /> : null}
                    </div>
                    {hospResults.length > 0 ? (
                      <ul className="mt-1 max-h-44 overflow-y-auto divide-y divide-slate-100 rounded border border-slate-200 bg-white">
                        {hospResults.map((h) => (
                          <li key={h.id}>
                            <button type="button" onClick={() => confirmHospital(h)}
                              className="w-full text-left px-2.5 py-1.5 hover:bg-emerald-50"
                              data-testid={`po-hospital-result-${h.id}`}>
                              <p className="text-[12px] font-semibold text-slate-900">
                                {h.name}
                                {h.is_network ? <span className="ml-1.5 text-[9px] font-black uppercase text-cyan-700">network</span> : null}
                              </p>
                              <p className="text-[10px] text-slate-500">
                                {[h.legal_entity_name, [h.city, h.state].filter(Boolean).join(", "),
                                  h.facility_count ? `${h.facility_count} facility(ies)` : null,
                                ].filter(Boolean).join(" · ") || h.id}
                              </p>
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-[11px] text-slate-500 mt-1">
                        {hospSearching ? "Searching…" : "No hospital matches that search."}
                      </p>
                    )}
                  </div>

                  <label className="mt-2 flex items-center gap-2 text-[11px] font-semibold text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={newCustomer}
                      onChange={(e) => setNewCustomer(e.target.checked)}
                      data-testid="po-new-customer-checkbox" />
                    Not in our database — file it under the name on the PO
                  </label>
                </>
              )}
            </div>

            {/* ── Which dock? Ben Taub and LBJ are the same customer. ───────── */}
            {hospital && facMatch && facMatch.facility_count > 0 ? (
              <div className={`rounded-lg border p-3 ${facility || shipAsPrinted
                ? "border-emerald-300 bg-emerald-50/50" : "border-amber-300 bg-amber-50/60"}`}
                data-testid="po-shipto-confirm">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <p className="text-[11px] font-black uppercase tracking-wider inline-flex items-center gap-1.5 text-slate-700">
                      <MapPin className="w-3.5 h-3.5" />
                      Which facility does it ship to?
                      {facility || shipAsPrinted
                        ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        : <span className="text-amber-700">· confirmation required</span>}
                    </p>
                    <p className="text-[11px] text-slate-600 mt-0.5">
                      {hospital.name} has {facMatch.facility_count} facility(ies) on file. The PO ships to a
                      dock, not to a health system — so confirm the campus before the warehouse picks it.
                    </p>
                  </div>
                  {facMatch.source === "alias" ? (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-white
                      ring-1 ring-emerald-300 rounded-full px-2 py-1 inline-flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> Mapped before
                    </span>
                  ) : null}
                </div>

                {p.ship_to ? (
                  <div className="mt-2 rounded bg-white border border-slate-200 p-2">
                    <span className="block text-[9px] font-black uppercase tracking-wider text-slate-400">
                      Ship-to block as printed on the PO
                    </span>
                    <span className="text-[11px] text-slate-700 whitespace-pre-line">{p.ship_to}</span>
                  </div>
                ) : (
                  <p className="mt-2 text-[11px] text-amber-800">
                    The PO has no readable ship-to block — pick the facility yourself.
                  </p>
                )}

                {facility ? (
                  <div className="mt-2 flex items-center justify-between gap-3 rounded bg-white ring-1 ring-emerald-300 p-2.5"
                    data-testid="po-shipto-chosen">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-slate-900 inline-flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-emerald-600" /> {facility.name}
                      </p>
                      <p className="text-[11px] text-slate-500">
                        {[facility.address, [facility.city, facility.state, facility.zip_code]
                          .filter(Boolean).join(" ")].filter(Boolean).join(" · ")}
                      </p>
                      {(facility.why || []).length ? (
                        <p className="text-[10px] text-emerald-800 mt-0.5">{facility.why.join(" · ")}</p>
                      ) : null}
                    </div>
                    <Button type="button" size="sm" variant="ghost"
                      className="h-7 px-2 text-[11px] text-slate-500 hover:bg-slate-100"
                      onClick={() => setFacility(null)}
                      data-testid="po-shipto-change-btn">
                      <X className="w-3 h-3 mr-1" /> Change
                    </Button>
                  </div>
                ) : shipAsPrinted ? (
                  <div className="mt-2 rounded bg-white ring-1 ring-emerald-300 p-2.5">
                    <p className="text-[12px] text-slate-700">
                      Shipping to the address exactly as printed on the PO — no facility on file is linked
                      to this order.
                    </p>
                  </div>
                ) : (
                  <>
                    {facMatch.resolved && facMatch.source === "match" ? (
                      <div className="mt-2 rounded bg-white ring-2 ring-cyan-400 p-2.5 flex items-start justify-between gap-3 flex-wrap"
                        data-testid="po-shipto-suggestion">
                        <div className="min-w-0">
                          <p className="text-[9px] font-black uppercase tracking-wider text-cyan-700">
                            Closest facility to the PO's ship-to
                          </p>
                          <p className="text-sm font-bold text-slate-900">{facMatch.resolved.name}</p>
                          <p className="text-[11px] text-slate-500">
                            {(facMatch.resolved.why || []).join(" · ") || facMatch.resolved.address}
                          </p>
                        </div>
                        <Button type="button" size="sm"
                          className="bg-cyan-600 hover:bg-cyan-700 text-white h-8"
                          onClick={() => setFacility(facMatch.resolved)}
                          data-testid="po-shipto-confirm-suggestion-btn">
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Yes, that dock
                        </Button>
                      </div>
                    ) : null}

                    <div className="mt-2">
                      <p className="text-[9px] font-black uppercase tracking-wider text-slate-500 mb-1">
                        Facilities on file for {hospital.name}
                      </p>
                      <ul className="max-h-48 overflow-y-auto divide-y divide-slate-100 rounded border border-slate-200 bg-white">
                        {(facMatch.facilities || []).map((f) => (
                          <li key={f.id}>
                            <button type="button" onClick={() => setFacility(f)}
                              className="w-full text-left px-2.5 py-1.5 hover:bg-emerald-50"
                              data-testid={`po-shipto-option-${f.id}`}>
                              <p className="text-[12px] font-semibold text-slate-900">
                                {f.name}
                                {f.is_main_facility ? (
                                  <span className="ml-1.5 text-[9px] font-black uppercase text-cyan-700">main</span>
                                ) : null}
                                {f.score >= 45 ? (
                                  <span className="ml-1.5 text-[9px] font-black uppercase text-emerald-700">likely</span>
                                ) : null}
                              </p>
                              <p className="text-[10px] text-slate-500">
                                {[f.address, [f.city, f.state, f.zip_code].filter(Boolean).join(" ")]
                                  .filter(Boolean).join(" · ") || f.id}
                              </p>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <label className="mt-2 flex items-center gap-2 text-[11px] font-semibold text-slate-700 cursor-pointer">
                      <input type="checkbox" checked={shipAsPrinted}
                        onChange={(e) => setShipAsPrinted(e.target.checked)}
                        data-testid="po-shipto-as-printed-checkbox" />
                      Not one of these — ship to the address exactly as printed on the PO
                    </label>
                  </>
                )}
              </div>
            ) : null}

            <div className="grid sm:grid-cols-4 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">
                  Filed under {hospital ? "(from our hospital database)" : newCustomer ? "(new customer)" : ""}                </label>
                <Input value={hospital ? hospital.name : form.hospital_name}
                  readOnly={!!hospital}
                  onChange={(e) => setForm({ ...form, hospital_name: e.target.value })}
                  className={hospital ? "bg-slate-100 text-slate-700" : ""}
                  data-testid="po-customer-input" />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Contact</label>
                <Input value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Contact email</label>
                <Input value={form.contact_email} placeholder="buyer@hospital.org"
                  onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  data-testid="po-contact-email-input" />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Freight %</label>
                <Input type="number" value={form.freight_percent}
                  onChange={(e) => setForm({ ...form, freight_percent: e.target.value })}
                  data-testid="po-freight-input" />
                <p className="text-[10px] text-slate-500 mt-0.5">
                  {p.shipping_account ? "Their PO names a third-party freight account — set 0 if they pay it." : "Applied on top of the PO's product total."}
                </p>
              </div>
            </div>

            {/* ── Who are we buying from? Optional, saved permanently. ─────── */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="po-supplier-block">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-wider text-slate-600 inline-flex items-center gap-1.5">
                    <Truck className="w-3.5 h-3.5" /> Supplier (optional)
                  </p>
                  <p className="text-[11px] text-slate-600 mt-0.5">
                    Award it now and skip the trip to the award panel. No email goes to the
                    supplier — the order is just stamped.
                  </p>
                </div>
                <Button type="button" size="sm" variant="ghost"
                  className="h-7 text-[11px] text-cyan-800 hover:bg-cyan-50"
                  onClick={() => { setSupplierManual(m => !m); setSupplierId(""); }}
                  data-testid="po-supplier-toggle-manual">
                  {supplierManual ? "Pick from the list" : "Not on the list — type it"}
                </Button>
              </div>

              {supplierManual ? (
                <div className="grid sm:grid-cols-2 gap-2 mt-2">
                  <Input className="bg-white" placeholder="Supplier name"
                    value={supplierName} onChange={(e) => setSupplierName(e.target.value)}
                    data-testid="po-supplier-name-input" />
                  <Input className="bg-white" placeholder="orders@supplier.com"
                    value={supplierEmail} onChange={(e) => setSupplierEmail(e.target.value)}
                    data-testid="po-supplier-email-input" />
                  <p className="sm:col-span-2 text-[10px] text-emerald-800">
                    Typed suppliers are saved to the supplier list for good — you will not retype
                    them next month.
                  </p>
                </div>
              ) : (
                <select
                  value={supplierId}
                  onChange={(e) => {
                    const s = suppliers.find(x => x.id === e.target.value);
                    setSupplierId(e.target.value);
                    setSupplierName(s?.name || s?.contact_name || "");
                    setSupplierEmail(s?.email || "");
                  }}
                  className="mt-2 w-full text-sm rounded border border-slate-300 bg-white px-2 py-2"
                  data-testid="po-supplier-select">
                  <option value="">— No supplier yet —</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name || s.contact_name}{s.email ? ` · ${s.email}` : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {(p.confidence_notes || []).length > 0 && (
              <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 inline-flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span><strong>Check these:</strong> {p.confidence_notes.join(" · ")}</span>
              </p>
            )}

            {result?.file ? (
              <a href={`${API}${result.file.url}`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-700 hover:underline">
                <Eye className="w-3 h-3" /> Open the PO we read ({result.file.name})
              </a>
            ) : null}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }}>Cancel</Button>
          {p ? (
            <>
              <Button variant="outline" onClick={reset} data-testid="po-start-over-btn">Read another PO</Button>
              <Button className="bg-violet-600 hover:bg-violet-700 text-white"
                onClick={create} disabled={creating || !readyToBuild}
                title={readyToBuild ? "" : needsShipConfirm
                  ? "Confirm which facility this PO ships to first"
                  : "Confirm which hospital this PO belongs to first"}
                data-testid="po-create-order-btn">
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Build the order
              </Button>
            </>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
